import { useMemo } from 'react'
import { useSearchParams } from 'react-router-dom'
import { useOpsDatasetsData, type NewDatasetInput, type NewItemInput } from './OpsDatasetsContext'
import type { Dataset, DatasetItem, GoldenExtraction } from './mockData'

export type DatasetsModal = 'new-dataset' | 'add-item' | null

export type UseDatasetsPanelReturn = {
  datasets: Dataset[]
  /** The dataset selected via `?dataset=`, or null when showing the list. */
  selectedDataset: Dataset | null
  /** Items in the selected dataset (empty when none selected). */
  items: DatasetItem[]
  /** Item counts keyed by dataset id, for the list view. */
  itemCounts: Record<string, number>
  /** The item whose drawer is open via `?item=`, or null. */
  focusedItem: DatasetItem | null
  /** Whether the focused item's golden is in edit mode (`?edit=1`). */
  editing: boolean
  /** Which modal is open (`?modal=`). */
  modal: DatasetsModal
  selectDataset: (id: string) => void
  clearDataset: () => void
  selectItem: (id: string) => void
  clearItem: () => void
  startEditing: () => void
  stopEditing: () => void
  openModal: (modal: Exclude<DatasetsModal, null>) => void
  closeModal: () => void
  createDataset: (input: NewDatasetInput) => void
  addItem: (input: NewItemInput) => void
  updateGolden: (itemId: string, golden: GoldenExtraction) => void
}

export function useDatasetsPanel(): UseDatasetsPanelReturn {
  const { datasets, items, itemsFor, createDataset, addItem, updateGolden } = useOpsDatasetsData()
  const [searchParams, setSearchParams] = useSearchParams()

  const datasetId = searchParams.get(`dataset`)
  const itemId = searchParams.get(`item`)
  const editing = searchParams.get(`edit`) === `1`
  const modal = (searchParams.get(`modal`) as DatasetsModal) ?? null

  const selectedDataset = useMemo(() => datasets.find((entry) => entry.id === datasetId) ?? null, [datasets, datasetId])

  const datasetItems = useMemo(() => (selectedDataset ? itemsFor(selectedDataset.id) : []), [selectedDataset, itemsFor])

  const itemCounts = useMemo(() => {
    const counts: Record<string, number> = {}
    for (const dataset of datasets) counts[dataset.id] = itemsFor(dataset.id).length
    return counts
  }, [datasets, itemsFor])

  const focusedItem = useMemo(
    () => (itemId ? (items.find((entry) => entry.id === itemId) ?? null) : null),
    [items, itemId],
  )

  const patch = (mutate: (next: URLSearchParams) => void) => {
    const next = new URLSearchParams(searchParams)
    mutate(next)
    setSearchParams(next)
  }

  return {
    datasets,
    selectedDataset,
    items: datasetItems,
    itemCounts,
    focusedItem,
    editing,
    modal,
    selectDataset: (id) => setSearchParams({ dataset: id }),
    clearDataset: () => setSearchParams({}),
    selectItem: (id) =>
      patch((next) => {
        next.set(`item`, id)
        next.delete(`edit`)
      }),
    clearItem: () =>
      patch((next) => {
        next.delete(`item`)
        next.delete(`edit`)
      }),
    startEditing: () => patch((next) => next.set(`edit`, `1`)),
    stopEditing: () => patch((next) => next.delete(`edit`)),
    openModal: (value) => patch((next) => next.set(`modal`, value)),
    closeModal: () => patch((next) => next.delete(`modal`)),
    createDataset,
    addItem,
    updateGolden,
  }
}
